import type {ReactElement} from 'react';
import {useEffect} from 'react';
import {DropdownGhostSelectBox} from '@Components/dropdown-v2/components/dropdown-ghost-select-box';
import {DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {EventDropdown} from '@Components/event-wizard/event-dropdown';
import type {RecurringMonth} from '@Components/event-wizard/month-day-selectors/month-day-selectors.types';
import {getWeekdayOrdinalFromDate} from '@Components/event-wizard/month-day-selectors/month-day-selectors.types';
import {capitalize} from '@Utils/string.util';
import {onEventWizardRecurringMonthWeekdayChange, onEventWizardRecurringMonthWeekdayOccurenceChange} from '@Components/event-wizard/event-wizard-slice';
import {
  useEventWizardEndDateTime,
  useEventWizardRecurringData,
  useEventWizardRecurringMonthOccurence,
  useEventWizardRecurringMonthWeekday,
  useEventWizardStartDateTime,
  useEventWizardTimeZone,
} from '@Components/event-wizard/event-wizard.hooks';
import styles from './month-day-selectors.module.scss';
import {useAppDispatch} from '@/hooks';
import {ALL_WEEKDAYS_USA, type Weekdays} from '@Components/event-wizard/week-day-selectors/week-day-selectors.types';
import {updatePreviewDateTime} from '@Components/event-wizard/preview/preview.helper';

export function MonthDaySelectors(): ReactElement {
  const dispatch = useAppDispatch();
  const recurringMonthOrdinal = useEventWizardRecurringMonthOccurence();
  const recurringMonthWeekday = useEventWizardRecurringMonthWeekday();
  const selectedStartDateTime = useEventWizardStartDateTime();
  const selectedEndDateTime = useEventWizardEndDateTime();
  const recurringPattern = useEventWizardRecurringData();
  const selectedTimezone = useEventWizardTimeZone();

  useEffect(() => {
    onUpdateWeekday(ALL_WEEKDAYS_USA[selectedStartDateTime.getDay()]);
    onUpdateWeekdayOrdinal(getWeekdayOrdinalFromDate(selectedStartDateTime));
  }, [selectedStartDateTime]);

  const onUpdateWeekdayOrdinal = (weekdayOrdinal: string): void => {
    dispatch(onEventWizardRecurringMonthWeekdayOccurenceChange(weekdayOrdinal));

    const updatedMonthlyPatern: RecurringMonth = {
      ...recurringPattern.recurringMonthly,
      weekdayNum: weekdayOrdinal,
    };
    updatePreviewDateTimeFromUpdateInMonthData(updatedMonthlyPatern);
  };

  const onUpdateWeekday = (weekday: Weekdays): void => {
    dispatch(onEventWizardRecurringMonthWeekdayChange(weekday));

    const updatedMonthlyPatern: RecurringMonth = {
      ...recurringPattern.recurringMonthly,
      weekday: weekday,
    };
    updatePreviewDateTimeFromUpdateInMonthData(updatedMonthlyPatern);
  };

  const updatePreviewDateTimeFromUpdateInMonthData = (updatedMonthlyPatern: RecurringMonth): void => {
    updatePreviewDateTime({
      startDateTime: selectedStartDateTime,
      endDateTime: selectedEndDateTime,
      recurringPattern: {
        ...recurringPattern,
        recurringMonthly: {
          ...updatedMonthlyPatern,
        },
      },
      timeZone: selectedTimezone,
    });
  };

  const getNumWeekSelector = (): ReactElement => {
    return (
      <DropdownGhostSelectBox
        isDisabled
        size={DROPDOWN_SIZE.XSMALL}
        textClassName="spacing-m-r-2"
        className={`${styles.selector} ${styles.numWeekSelector}`}
        openedClassName={styles.dropdownSelectorOpened}
        text={capitalize(recurringMonthOrdinal)}
      />
    );
  };

  const getDayOfWeekSelector = (): ReactElement => {
    return (
      <DropdownGhostSelectBox
        isDisabled
        size={DROPDOWN_SIZE.XSMALL}
        textClassName="spacing-m-r-2"
        className={`${styles.selector} ${styles.dayOfWeekSelector}`}
        openedClassName={styles.dropdownSelectorOpened}
        text={capitalize(recurringMonthWeekday)}
      />
    );
  };

  return (
    <div className="flex-row-align-center">
      <div className="spacing-m-r-2">
        <EventDropdown selector={getNumWeekSelector()} popup={<></>} />
      </div>
      <EventDropdown selector={getDayOfWeekSelector()} popup={<></>} />
    </div>
  );
}
